import { Box, Button, Divider, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import SearchInput from '../../common/components/SearchInput';
import ClearAllButton from '../../common/components/ClearAllButton';
import useSearchProducts from './useSearchProducts';
import ProductSubFamilyFilter from './ProductSubFamilyFilter';
import ProductAvailabilityFilter, { AvailabilityFilterValues } from './ProductAvailabilityFilter';
import Sheet from '../../common/components/Sheet';
import { areArraysEqual } from '../../common/utils/common.utils';
import { productFiltersAtom } from '../list/state';

export interface DefaultValues {
  productFamilies: Product_Sub_Family_Name_Enum[];
  query: string;
  availability: AvailabilityFilterValues;
}

export interface ProductSearchBarProps {
  productFamiliesOptions: Product_Sub_Family_Name_Enum[];
  defaultValues?: Partial<DefaultValues>;
  sx?: SxProps<Theme>;
}

export default function ProductSearchBar({ productFamiliesOptions, sx, defaultValues }: ProductSearchBarProps): React.JSX.Element {
  const [productFilters, setProductFilters] = useAtom(productFiltersAtom);
  const handleProductSearchChange = useCallback((productFamilies: Product_Sub_Family_Name_Enum[], query: string, availability: AvailabilityFilterValues): void => {
    if (areArraysEqual(productFamilies, productFilters.productFamilies) && query === productFilters.query && availability === productFilters.availability) {
      return;
    }

    setProductFilters({ productFamilies, query, page: 1, availability });
  }, [productFilters]);
  const {
    setQuery,
    setProductFamilies,
    selectedProductFamilyMap,
    productFamilyMap,
    query,
    availabilityFilter,
    setAvailabilityFilter,
  } = useSearchProducts({ productFamiliesOptions, onChange: handleProductSearchChange, defaultValues });

  const clearAll = (): void => {
    setQuery('');
    setProductFamilies([]);
    setAvailabilityFilter('ALL');
  };
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation(['common', 'products']);

  const sheetComponent = (
    <Sheet
      open={drawerOpen}
      width={400}
      onClose={() => setDrawerOpen(false)}
      title={t('common:filters')}
    >

      <Box
        sx={{
          height: '100%',
          mt: 1.5,
          overflow: 'auto',
        }}
      >
        <ProductSubFamilyFilter
          productFamilyMap={productFamilyMap}
          selectedProductFamilyMap={selectedProductFamilyMap}
          onChange={(values) => setProductFamilies(values.map(({ value }) => value))}
          mode="ACCORDION"
        />
        <Divider />
        <ProductAvailabilityFilter
          availabilityFilter={availabilityFilter}
          onChange={(value) => setAvailabilityFilter(value)}
          mode="ACCORDION"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Divider />
        <Button
          variant="contained"
          onClick={() => setDrawerOpen(false)}
          sx={{
            mt: 2,
            marginLeft: 'auto',
          }}
        >
          {t('common:confirm')}
        </Button>
      </Box>
    </Sheet>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        ...sx,
      }}
    >
      <SearchInput
        onChange={(inputQuery) => setQuery(inputQuery)}
        value={query}
        sx={{
          width: {
            xs: '100%',
            lg: 400,
          },
        }}
      />
      {isDownLg
        ? (
          <>
            <Button onClick={() => setDrawerOpen(true)}>
              <TuneOutlinedIcon />
            </Button>
            {sheetComponent}
          </>
        )
        : (
          <>
            <Divider
              orientation="vertical"
              sx={{
                height: 24,
                display: {
                  xs: 'none',
                  sm: 'flex',
                },
              }}
            />
            <ProductSubFamilyFilter
              productFamilyMap={productFamilyMap}
              selectedProductFamilyMap={selectedProductFamilyMap}
              onChange={(values) => setProductFamilies(values.map(({ value }) => value))}
            />
            <ProductAvailabilityFilter
              onChange={(value) => setAvailabilityFilter(value)}
              availabilityFilter={availabilityFilter}
            />
          </>
        )}

      {
        !!(query.length || selectedProductFamilyMap.length || availabilityFilter !== 'ALL') && !isDownLg && (
        <ClearAllButton
          onClick={clearAll}
        />
        )
      }
    </Box>
  );
}
